import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import PropTypes from "prop-types";

export default function CardContactDetail({ contact, isOpen, onClose }) {
  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen || !contact) return null;

  const getUserTypeBadge = (userType) => {
    if (!userType) return <span className="text-blueGray-400 text-xs">No especificado</span>;

    const badges = {
      solo_developer: { color: "bg-blue-500", text: "Solo Developer" },
      entrepreneur: { color: "bg-green-500", text: "Entrepreneur" },
      startup_team: { color: "bg-purple-500", text: "Startup Team" }
    };

    const badge = badges[userType] || { color: "bg-blueGray-500", text: userType };

    return (
      <span className={`${badge.color} text-white text-xs px-2 py-1 rounded-full`}>
        {badge.text}
      </span>
    );
  };

  const getContactTypeBadge = (type) => {
    const badges = {
      contact_form: { color: "bg-lightBlue-500", text: "Contacto", icon: "fa-envelope" },
      quick_signup: { color: "bg-orange-500", text: "Suscripción", icon: "fa-bolt" }
    };

    const badge = badges[type] || { color: "bg-blueGray-500", text: type, icon: "fa-question" };

    return (
      <span className={`${badge.color} text-white text-xs px-2 py-1 rounded-full`}>
        <i className={`fas ${badge.icon} mr-1`}></i>
        {badge.text}
      </span>
    );
  };

  const getDaysSinceBadge = (days) => {
    let color = "bg-green-500";
    if (days > 7) color = "bg-orange-500";
    if (days > 14) color = "bg-red-500";

    return (
      <span className={`${color} text-white text-xs px-2 py-1 rounded-full`}>
        {days} {days === 1 ? 'día' : 'días'}
      </span>
    );
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return createPortal(
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        zIndex: 99999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1rem'
      }}
      onClick={onClose}
    >
      <div
        className="relative bg-white rounded-lg shadow-lg outline-none focus:outline-none w-full max-w-3xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between p-5 border-b border-solid border-blueGray-200 rounded-t">
          <h3 className="text-2xl font-semibold">
            Detalle del Contacto
          </h3>
          <button
            className="p-1 ml-auto bg-transparent border-0 text-black float-right text-3xl leading-none font-semibold outline-none focus:outline-none"
            onClick={onClose}
          >
            <span className="bg-transparent text-black h-6 w-6 text-2xl block outline-none focus:outline-none">
              ×
            </span>
          </button>
        </div>

        {/* Body */}
        <div className="relative p-6 flex-auto">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-blueGray-600 text-xs font-bold mb-2">
                NOMBRE
              </label>
              <p className="text-blueGray-700">{contact.name || "—"}</p>
            </div>
            <div>
              <label className="block text-blueGray-600 text-xs font-bold mb-2">
                EMAIL
              </label>
              <p className="text-blueGray-700">{contact.email}</p>
            </div>
            <div>
              <label className="block text-blueGray-600 text-xs font-bold mb-2">
                TIPO DE USUARIO
              </label>
              <p>{getUserTypeBadge(contact.user_type)}</p>
            </div>
            <div>
              <label className="block text-blueGray-600 text-xs font-bold mb-2">
                TIPO DE CONTACTO
              </label>
              <p>{getContactTypeBadge(contact.type)}</p>
            </div>
            <div>
              <label className="block text-blueGray-600 text-xs font-bold mb-2">
                FECHA
              </label>
              <p className="text-blueGray-700">{formatDate(contact.created_at)}</p>
            </div>
            <div>
              <label className="block text-blueGray-600 text-xs font-bold mb-2">
                DÍAS DESDE CONTACTO
              </label>
              <p>{getDaysSinceBadge(contact.days_since_contact)}</p>
            </div>
            <div className="col-span-2">
              <label className="block text-blueGray-600 text-xs font-bold mb-2">
                MENSAJE
              </label>
              <p className="text-blueGray-700 whitespace-pre-wrap">
                {contact.message || "—"}
              </p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end p-6 border-t border-solid border-blueGray-200 rounded-b">
          <button
            className="bg-emerald-500 text-white active:bg-emerald-600 font-bold uppercase text-sm px-6 py-3 rounded shadow hover:shadow-lg outline-none focus:outline-none mr-1 mb-1 ease-linear transition-all duration-150"
            type="button"
            onClick={() => {
              navigator.clipboard.writeText(contact.email);
              alert('Email copiado al portapapeles');
            }}
          >
            <i className="fas fa-copy mr-2"></i>
            Copiar Email
          </button>
          <button
            className="text-blueGray-500 background-transparent font-bold uppercase px-6 py-2 text-sm outline-none focus:outline-none mr-1 mb-1 ease-linear transition-all duration-150"
            type="button"
            onClick={onClose}
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

CardContactDetail.propTypes = {
  contact: PropTypes.object,
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};
